// @ts-check
import moment from 'moment';
/**
 * @typedef {import("moment").Moment} Moment
 */

const searchDateParamFormat = 'YYYY-MM-DD';
const legacySearchDateParamFormat = 'DD-MMM-YY';

/**
 * Shape check for a legacy DD-MMM-YY date segment. The month segment is tolerant of any
 * 2-10 non-slash characters (not just 3 ASCII letters), since the app's per-tenant/locale
 * month abbreviations (set in `initMoment.js`, e.g. pt-BR "Maio", fr-FR "Août"/"Sept", zh-CN
 * "5月") vary in length and script — this is shape only, `isValidSearchDate` can't check the
 * month's real calendar validity for the legacy format without knowing which locale
 * generated it (see its own doc comment). The day segment, unlike the month, is
 * locale-independent, so it's bounded to 01-31 (`00`/`32`-`99` are never a valid day in any
 * locale) even though this remains a shape check, not a full calendar check (a day like `31`
 * still isn't verified against the actual month/year).
 */
const LEGACY_SEARCH_DATE_SHAPE = /^(0[1-9]|[12]\d|3[01])-[^/]{2,10}-\d{2}$/;

/**
 * Full React Router path for the search-results/provider-selection route, shared between
 * `App.js` (the real route declaration) and its routing test, so the test exercises the
 * exact same path contract instead of a hand-rebuilt copy.
 *
 * `:departureDate`/`:returnDate` are deliberately unconstrained here — an earlier version
 * added a date-shape regex constraint, but that made the route itself a second, inconsistent
 * date gate: a shape-invalid segment (e.g. "Invalid Date") would fail to match at all and
 * fall through to the catch-all NotFound route, while a shape-valid-but-impossible one (e.g.
 * "2024-02-31") would match and reach `ValidatedSearchProcessor`, which redirects instead of
 * 404ing. Two gates, two different failure UX, for what should be one decision. Validating
 * date segments is entirely `ValidatedSearchProcessor`'s job now (see
 * `src/components/search/SearchProcessor/ValidatedSearchProcessor.js`) — the route matches
 * any string, so every case reaches that single, consistent guard.
 */
const SEARCH_RESULTS_ROUTE_PATH =
  '/search/:originSlug/:destinationSlug/:departureDate/:returnDate?/p/:passengers/:way(departures)?/:isOpen(open)?';

/**
 *
 * Get moment object from a string date with format YYYY-MM-DD, used to parse
 * search date params (departure and return dates) from url
 * @param {string} searchDate - date string with format YYYY-MM-DD
 * @returns {Moment} moment object corresponding to date input
 *
 * @example
 *
 * ```js
 * const { departureDate } = match.params // '2019-01-01'
 * const departureMoment = getMomentFromSearchDateParam(departureDate)
 * // Do something with the moment object
 * departureMoment.format('DD-MM-YYYY') // '01-01-2019'
 * ```
 */
const getMomentFromSearchDateParam = (searchDate) => {
  const parsedDateMoment = moment(searchDate, searchDateParamFormat);
  if (parsedDateMoment.isValid()) {
    return parsedDateMoment;
  }
  // If the date is not valid, try to parse it with the legacy format
  return moment(searchDate, legacySearchDateParamFormat);
};

/**
 * Get date string with format YYYY-MM-DD from a moment object, used to create
 * search date params (departure and return dates) for url
 *
 * @param {Moment} momentObj - moment object
 * @returns {string} date string with format YYYY-MM-DD
 *
 * @example
 * ```js
 * const { departureMoment } = searchFormFields
 * const departureDate = getSearchDateParamFromMoment(departureMoment)
 * // Do something with the date string
 * console.log(departureDate) // '2019-01-01'
 * ```
 */
const getSearchDateParamFromMoment = (momentObj) => momentObj.format(searchDateParamFormat);

/**
 * Determines whether a date string is a valid search date: real calendar validity for the
 * current format (YYYY-MM-DD, locale-independent), or just the DD-MMM-YY shape for the
 * legacy format — the month abbreviation's language/script isn't knowable here (it depends
 * on which per-tenant locale override in `initMoment.js` generated it), so this can't check
 * whether it's a real month name, only that the segment has the right shape.
 *
 * @param {string} date - date string to validate
 * @returns {boolean} whether the date string is a valid search date
 *
 * @example
 * ```js
 * isValidSearchDate('2024-03-25') // true
 * isValidSearchDate('05-Maio-24') // true (shape-valid; not calendar-checked)
 * isValidSearchDate('Invalid Date') // false
 * ```
 */
const isValidSearchDate = (date) => {
  if (!date) return false;
  if (moment(date, searchDateParamFormat, true).isValid()) return true;
  return LEGACY_SEARCH_DATE_SHAPE.test(date);
};

/**
 * Determines if param date is a valid date.
 * If param date is valid it is returned.
 * If not returns current day date.
 *
 * @param {string} date - date string with format DD-MM-YYYY
 * @returns {string} date string with format DD-MM-YYYY
 *
 * @example
 * ```js
 * const invalidDate = '32-01-2024'
 * const validDate = getValidDate(invalidDate)
 * console.log(validDate) // '25-03-2024'
 * ```
 */
const getValidDate = (date) => {
  if (isValidSearchDate(date)) {
    return date;
  }
  return moment().format(searchDateParamFormat);
};

export {
  getMomentFromSearchDateParam,
  getSearchDateParamFromMoment,
  getValidDate,
  isValidSearchDate,
  SEARCH_RESULTS_ROUTE_PATH,
};
